import React, { useRef, useMemo } from 'react';

import {
  ErrorBoundary,
  SearchProvider,
  Results as AppSearchResults,
  WithSearch,
} from '@elastic/react-search-ui';
import { ConfigHelper } from '../../config/ConfigHelper';
import HeApiConnector from '../../connectors/HeApiConnector';
import { ConnectorProvider } from '../../connectors/ConnectorContext';
import DocumentMetaUpdater from '../../components/DocumentMetaUpdater';
import configJson from './har-search-config.json';
import PagingRealistic from '../../components/PagingRealistic';
import HARSearchResultView from './HARSearchResultView';
import Layout from '../../components/Layout';
import HARSearchFilters from './HARSearchFilters';
import Results from '../../components/Results';
import ResultHeader from '../../components/ResultHeader';
import NoResults from '../../components/NoResults';
import Header from '../../components/Header';
import FiltersButton from '../../components/FiltersButton';
import ErrorBoundaryContent from '../../components/ErrorBoundaryContent';
import HelpFooter from '../../components/HelpFooter';
import { MapStateProvider } from '../../contexts/MapStateContext';

const configHelper = new ConfigHelper(configJson);

export default function HARSearch() {
  const mapStateRef = useRef({
    isMapMode: false,
    zoom: 6,
    bounds: null,
    visibleCount: null,
  });

  const connector = useMemo(
    () => new HeApiConnector(configHelper.getEndpoints('HAR')),
    [],
  );

  const config = useMemo(
    () => ({
      alwaysSearchOnInitialLoad: true,
      apiConnector: connector,
      initialState: {
        resultsPerPage: configHelper.getPageSizeOptions()[0],
      },
      searchQuery: {
        facets: configHelper.buildFacetConfigFromConfig(),
        ...configHelper.buildSearchOptionsFromConfig(),
        disjunctiveFacets: configHelper.buildDisjunctiveFacetsFromConfig(),
      },
    }),
    [connector],
  );

  return (
    <SearchProvider config={config}>
      <ConnectorProvider connector={connector}>
        <MapStateProvider externalRef={mapStateRef}>
          <DocumentMetaUpdater searchName="Heritage at Risk Register" />
          <WithSearch
            mapContextToProps={({
              clearFilters,
              facets,
              filters,
              isLoading,
              wasSearched,
              totalResults,
              resultSearchTerm,
            }) => ({
              clearFilters,
              facets,
              filters,
              isLoading,
              wasSearched,
              totalResults,
              resultSearchTerm,
            })}
          >
            {({
              clearFilters,
              facets,
              filters,
              isLoading,
              wasSearched,
              totalResults,
            }) => {
              return (
                <div className="search-ui" id="main-content">
                  <ErrorBoundary view={ErrorBoundaryContent}>
                    <Layout
                      BodyContent={
                        <AppSearchResults
                          resultView={HARSearchResultView}
                          shouldTrackClickThrough={true}
                          thumbnailField={configHelper.getThumbnailField()}
                          titleField={configHelper.getTitleField()}
                          urlField={configHelper.getUrlField()}
                          view={Results}
                        />
                      }
                      BodyFooter={
                        <>
                          <PagingRealistic />
                          <HelpFooter />
                        </>
                      }
                      BodyHeader={
                        <ResultHeader
                          configHelper={configHelper}
                          connector={connector}
                          filters={filters}
                          totalResults={totalResults}
                          wasSearched={wasSearched}
                        />
                      }
                      Filters={<HARSearchFilters />}
                      Header={
                        <Header
                          FiltersButton={
                            <FiltersButton
                              SearchFilters={<HARSearchFilters />}
                              clearFilters={clearFilters}
                              facets={facets}
                            />
                          }
                        />
                      }
                      NoResults={<NoResults />}
                      filters={filters}
                      isLoading={isLoading}
                      searchType="har"
                      totalResults={totalResults}
                      wasSearched={wasSearched}
                    />
                  </ErrorBoundary>
                </div>
              );
            }}
          </WithSearch>
        </MapStateProvider>
      </ConnectorProvider>
    </SearchProvider>
  );
}
